import React, { FC, ReactElement } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import { Box, DialogContent, DialogContentText } from '@material-ui/core';
import DialogTitle from '@material-ui/core/DialogTitle';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import Yup from 'common/utils/yup-extended.utils';
import { Button, TextField } from 'common/components';

interface MessageDialogProps {
    message?: string;
    title?: string;
    onConfirm: (message: string) => void;
    onCancel?: () => void;
}

export const MessageDialog: FC<MessageDialogProps> = ({
    children,
    message,
    title,
    onConfirm,
    onCancel,
}) => {
    const { t } = useTranslation();
    const [open, setOpen] = React.useState(false);

    const handleClose = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setOpen(false);
        onCancel?.();
    };

    const form = useFormik({
        initialValues: {
            message: '',
        },
        validationSchema: Yup.object({
            message: Yup.string()
                .min(5, t('min-required', { ns: 'validation', value: 5 }))
                .required(t('required-field', { ns: 'validation' })),
        }),
        onSubmit: (values) => {
            onConfirm(values.message);
            setOpen(false);
        },
    });

    const cloneChildren = React.cloneElement(children as ReactElement<any>, {
        onClick: (ev: any) => {
            // executa click do elemento clonado caso exista
            (children as any)?.props?.onClick?.(ev);
            ev.stopPropagation();
            setOpen(true);
        },
    });

    return (
        <>
            {cloneChildren}
            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby='alert-dialog-title'
                aria-describedby='alert-dialog-description'
                maxWidth='xs'
            >
                <Box p={1}>
                    {title && <DialogTitle>{title}</DialogTitle>}
                    {message && (
                        <DialogContent>
                            <DialogContentText color='textPrimary'>{message}</DialogContentText>
                        </DialogContent>
                    )}
                    <Box mr={3} ml={3} mb={2}>
                        <TextField
                            fullWidth
                            color='primary'
                            name='message'
                            defaultValue={form.values.message}
                            label={t('term.justification')}
                            onChange={form.handleChange}
                            variant='outlined'
                            multiline
                            inputProps={{
                                maxLength: 1000,
                            }}
                            autoFocus
                            minRows={3}
                            maxRows={4}
                        />
                    </Box>
                    <DialogActions>
                        <Button onClick={handleClose} color='primary' size='small'>
                            {t('term.cancel')}
                        </Button>
                        <Button
                            variant='contained'
                            disabled={!form.dirty || !!Object.keys(form.errors).length}
                            color='primary'
                            onClick={(ev) => {
                                ev.stopPropagation();
                                form.submitForm();
                            }}
                            size='small'
                        >
                            {t('term.confirm')}
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>
        </>
    );
};
